import React, { useState, useEffect } from 'react';
import { Check, Loader2, User, Phone, MapPin, CreditCard, Building, MessageSquare } from 'lucide-react';

const PublicContactForm = () => {
  const [form, setForm] = useState({
    name: '',
    phone: '',
    cpf: '',
    cep: '',
    city: '',
    state: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [settings, setSettings] = useState(null);
  const [loadingCep, setLoadingCep] = useState(false);

  useEffect(() => {
    // Fetch settings for branding
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings`)
      .then(res => res.json())
      .then(data => setSettings(data))
      .catch(() => {});
  }, []);

  // Format phone number as user types
  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    if (numbers.length <= 11) return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  // Format CPF as user types
  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
    if (numbers.length <= 9) return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
    return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`;
  };

  // Format CEP as user types
  const formatCEP = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 5) return numbers;
    return `${numbers.slice(0, 5)}-${numbers.slice(5, 8)}`;
  };

  // Fetch address from CEP
  const fetchAddress = async (cep) => {
    const cleanCep = cep.replace(/\D/g, '');
    if (cleanCep.length !== 8) return;
    
    setLoadingCep(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data = await response.json();
      if (!data.erro) {
        setForm(prev => ({
          ...prev,
          city: data.localidade || '',
          state: data.uf || ''
        }));
      }
    } catch (err) {
      console.error('Error fetching CEP:', err);
    } finally {
      setLoadingCep(false);
    }
  };

  const handleCepChange = (value) => {
    const formatted = formatCEP(value);
    setForm(prev => ({ ...prev, cep: formatted }));
    
    // Auto-fetch address when CEP is complete
    if (value.replace(/\D/g, '').length === 8) {
      fetchAddress(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name.trim() || !form.phone.trim()) {
      setError('Por favor, preencha nome e telefone');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          phone: form.phone.replace(/\D/g, ''),
          cpf: form.cpf.replace(/\D/g, ''),
          cep: form.cep.replace(/\D/g, ''),
          city: form.city,
          state: form.state,
          summary: form.message,
          source: 'whatsapp_link'
        })
      });

      if (response.ok) {
        setSuccess(true);
      } else {
        const data = await response.json();
        setError(data.detail || 'Erro ao enviar. Tente novamente.');
      }
    } catch (err) {
      setError('Erro de conexão. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-teal-500 to-teal-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-2xl">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Cadastro Realizado!</h1>
          <p className="text-slate-600 mb-6">
            Obrigado pelo seu contato! Em breve entraremos em contato com você.
          </p>
          <div className="p-4 bg-teal-50 rounded-xl">
            <p className="text-sm text-teal-700">
              Você pode fechar esta página e voltar para o WhatsApp.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-500 to-teal-600">
      {/* Header */}
      <div className="pt-8 pb-6 px-4 text-center">
        {settings?.logo_url ? (
          <img 
            src={settings.logo_url} 
            alt={settings.company_name || 'Logo'} 
            className="h-12 mx-auto mb-3 brightness-0 invert"
          />
        ) : (
          <h1 className="text-2xl font-bold text-white mb-1">
            {settings?.company_name || 'Cadastro'}
          </h1>
        )}
        <p className="text-teal-100 text-sm">
          Preencha seus dados para entrarmos em contato
        </p>
      </div>

      {/* Form Card */}
      <div className="px-4 pb-8">
        <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-6 shadow-2xl max-w-md mx-auto">
          
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Seu Nome *
            </label>
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Digite seu nome completo"
                className="w-full pl-12 pr-4 py-4 border-2 border-slate-200 rounded-2xl text-lg focus:border-teal-500 focus:outline-none transition-colors"
                required
              />
            </div>
          </div>

          {/* Phone */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              WhatsApp *
            </label>
            <div className="relative">
              <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm(prev => ({ ...prev, phone: formatPhone(e.target.value) }))}
                placeholder="(00) 00000-0000"
                className="w-full pl-12 pr-4 py-4 border-2 border-slate-200 rounded-2xl text-lg focus:border-teal-500 focus:outline-none transition-colors"
                required
              />
            </div>
          </div>

          {/* CPF */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              CPF
            </label>
            <div className="relative">
              <CreditCard className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={form.cpf}
                onChange={(e) => setForm(prev => ({ ...prev, cpf: formatCPF(e.target.value) }))}
                placeholder="000.000.000-00"
                maxLength={14}
                className="w-full pl-12 pr-4 py-4 border-2 border-slate-200 rounded-2xl text-lg focus:border-teal-500 focus:outline-none transition-colors"
              />
            </div>
          </div>

          {/* CEP */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              CEP
            </label>
            <div className="relative">
              <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={form.cep}
                onChange={(e) => handleCepChange(e.target.value)}
                placeholder="00000-000"
                maxLength={9}
                className="w-full pl-12 pr-4 py-4 border-2 border-slate-200 rounded-2xl text-lg focus:border-teal-500 focus:outline-none transition-colors"
              />
              {loadingCep && (
                <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-teal-500 animate-spin" />
              )}
            </div>
          </div>

          {/* City/State - Auto filled */}
          {(form.city || form.state) && (
            <div className="mb-4 p-3 bg-teal-50 rounded-xl flex items-center gap-2">
              <Building className="w-5 h-5 text-teal-600" />
              <span className="text-teal-700">
                {form.city}{form.city && form.state ? ' / ' : ''}{form.state}
              </span>
            </div>
          )}

          {/* Message */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              O que você precisa?
            </label>
            <div className="relative">
              <MessageSquare className="absolute left-4 top-4 w-5 h-5 text-slate-400" />
              <textarea
                value={form.message}
                onChange={(e) => setForm(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Descreva brevemente o que você procura..."
                rows={3}
                className="w-full pl-12 pr-4 py-4 border-2 border-slate-200 rounded-2xl text-lg focus:border-teal-500 focus:outline-none transition-colors resize-none"
              />
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white text-lg font-semibold rounded-2xl transition-colors flex items-center justify-center gap-2 shadow-lg shadow-teal-600/30"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Check className="w-5 h-5" />
                Enviar Cadastro
              </>
            )}
          </button>

          <p className="text-center text-xs text-slate-400 mt-4">
            Seus dados estão seguros e não serão compartilhados.
          </p>
        </form>
      </div>
    </div>
  );
};

export default PublicContactForm;
